import Cookies from 'js-cookie';

import { logger } from 'utils/logger';

export const COOKIE_KEYS = {
  REFRESH_TOKEN: 'refresh_token',
  ACCESS_TOKEN: 'access_token',
  TOKEN_EXPIRY: 'token_expiry',
} as const;

export const COOKIE_OPTIONS = {
  REFRESH_TOKEN: {
    secure: true,
    sameSite: 'lax' as const,
    expires: 30, // 30 days
  },
  ACCESS_TOKEN: {
    secure: true,
    sameSite: 'lax' as const,
    expires: 1, // 1 day
  },
  TOKEN_EXPIRY: {
    secure: true,
    sameSite: 'lax' as const,
    expires: 1, // 1 day
  },
} as const;

export class CookieManager {
  /**
   * Sets the refresh token in a cookie
   */
  static setRefreshToken(token: string, options?: Cookies.CookieAttributes): void {
    const cookieOptions = {
      ...COOKIE_OPTIONS.REFRESH_TOKEN,
      ...options,
    };

    Cookies.set(COOKIE_KEYS.REFRESH_TOKEN, token, cookieOptions);

    logger.debug('[CookieManager] 🍪 Refresh token set in cookie', {
      tokenLength: token.length,
      options: cookieOptions,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Gets the refresh token from the cookie
   */
  static getRefreshToken(): string | undefined {
    const token = Cookies.get(COOKIE_KEYS.REFRESH_TOKEN);

    logger.debug('[CookieManager] 🔍 Fetching refresh token:', {
      cookieKey: COOKIE_KEYS.REFRESH_TOKEN,
      hasToken: !!token,
      tokenLength: token?.length || 0,
      tokenStart: token?.substring(0, 20) + '...' || 'N/A',
      allCookies: document.cookie,
      cookiesList: document.cookie.split(';').map(c => c.trim()),
    });

    if (token) {
      logger.debug('[CookieManager] ✅ Refresh token found in cookie');
    } else {
      logger.debug('[CookieManager] ❌ Refresh token not found in cookie');
    }
    return token;
  }

  /**
   * Removes the refresh token from the cookie
   */
  static removeRefreshToken(): void {
    Cookies.remove(COOKIE_KEYS.REFRESH_TOKEN);
    logger.debug('[CookieManager] Refresh token removed from cookie');
  }

  /**
   * Sets the access token in a cookie
   */
  static setAccessToken(token: string, options?: Cookies.CookieAttributes): void {
    const cookieOptions = {
      ...COOKIE_OPTIONS.ACCESS_TOKEN,
      ...options,
    };

    Cookies.set(COOKIE_KEYS.ACCESS_TOKEN, token, cookieOptions);

    logger.debug('[CookieManager] 🔑 Access token set in cookie', {
      tokenLength: token.length,
      options: cookieOptions,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Gets the access token from the cookie
   */
  static getAccessToken(): string | undefined {
    const token = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
    if (token) {
      logger.debug('[CookieManager] Access token found in cookie');
    } else {
      logger.debug('[CookieManager] Access token not found in cookie');
    }
    return token;
  }

  /**
   * Removes the access token from the cookie
   */
  static removeAccessToken(): void {
    Cookies.remove(COOKIE_KEYS.ACCESS_TOKEN);
    logger.debug('[CookieManager] Access token removed from cookie');
  }

  /**
   * Sets the token expiry date
   */
  static setTokenExpiry(expiryTimestamp: number, options?: Cookies.CookieAttributes): void {
    Cookies.set(COOKIE_KEYS.TOKEN_EXPIRY, expiryTimestamp.toString(), {
      ...COOKIE_OPTIONS.TOKEN_EXPIRY,
      ...options,
    });
    logger.debug('[CookieManager] Token expiry set:', new Date(expiryTimestamp));
  }

  /**
   * Gets the token expiry date
   */
  static getTokenExpiry(): number | undefined {
    const expiry = Cookies.get(COOKIE_KEYS.TOKEN_EXPIRY);
    if (expiry) {
      const timestamp = parseInt(expiry, 10);
      logger.debug('[CookieManager] Token expiry found:', new Date(timestamp));
      return timestamp;
    }
    logger.debug('[CookieManager] Token expiry not found');
    return undefined;
  }

  /**
   * Removes the token expiry date
   */
  static removeTokenExpiry(): void {
    Cookies.remove(COOKIE_KEYS.TOKEN_EXPIRY);
    logger.debug('[CookieManager] Token expiry removed');
  }

  /**
   * Clears all authentication cookies
   */
  static clearAllAuthCookies(): void {
    logger.debug('[CookieManager] 🧹 Starting cleanup of all authentication cookies...');

    this.removeRefreshToken();
    this.removeAccessToken();
    this.removeTokenExpiry();

    // Clear additional cookies that may exist
    const additionalCookies = [
      'auth_token',
      'refresh_token',
      'token_expiry',
      'user_session',
      'session_id',
      'auth_session',
    ];

    additionalCookies.forEach(cookieName => {
      Cookies.remove(cookieName);
      Cookies.remove(cookieName, { path: '/' });
      Cookies.remove(cookieName, { path: '/', domain: window.location.hostname });
    });

    logger.debug('[CookieManager] ✅ All authentication cookies have been cleared');
  }

  /**
   * Checks if any authentication token exists
   */
  static hasAnyAuthToken(): boolean {
    const hasAccess = !!this.getAccessToken();
    const hasRefresh = !!this.getRefreshToken();
    return hasAccess || hasRefresh;
  }

  /**
   * Gets information about all authentication cookies
   */
  static getAuthCookiesInfo(): {
    hasAccessToken: boolean;
    hasRefreshToken: boolean;
    hasExpiry: boolean;
    expiryDate?: Date;
  } {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();
    const expiry = this.getTokenExpiry();

    return {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      hasExpiry: !!expiry,
      expiryDate: expiry ? new Date(expiry) : undefined,
    };
  }

  /**
   * Checks if the refresh token is being set by the backend
   */
  static checkRefreshTokenFromBackend(): {
    isDefined: boolean;
    tokenValue?: string;
    tokenLength?: number;
    allCookies: string;
    cookieList: string[];
  } {
    const allCookies = document.cookie;
    const cookieList = allCookies.split(';').map(c => c.trim());
    const refreshToken = this.getRefreshToken();

    logger.debug('[CookieManager] 🔍 Complete refresh token check:', {
      isDefined: !!refreshToken,
      tokenValue: refreshToken,
      tokenLength: refreshToken?.length,
      allCookies,
      cookieList,
      cookieKeys: Object.keys(COOKIE_KEYS),
    });

    return {
      isDefined: !!refreshToken,
      tokenValue: refreshToken,
      tokenLength: refreshToken?.length,
      allCookies,
      cookieList,
    };
  }
}
